import { ChevronLeft, ChevronRight, Download, MousePointerClick } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";
import { thematicClasses } from "@/lib/thematic";

import {
  downloadCsv,
  useAssessmentData,
  useAssessmentSummary,
  useFilterOptions,
  useGroupEnterprises,
  useReportFacets,
} from "../api/hooks";
import {
  PROGRESS_STEPS,
  type AssessmentFilterState,
  type AssessmentLevel,
} from "../api/types";
import { QueryState } from "./QueryState";
import {
  AdvancedFilters,
  FilterChip,
  FilterField,
  MonthInput,
  MultiSelectFilter,
  filterControlClass,
} from "./ui";

// Sequential single-hue ramp (teal, light→dark by step index) — replaces the
// old ad-hoc STEP_COLORS. Magnitude encoding for the by-step progression;
// deliberately distinct from the four thematic identity hues.
const STEP_RAMP = [
  "hsl(200 35% 78%)",
  "hsl(199 42% 68%)",
  "hsl(198 50% 58%)",
  "hsl(197 58% 48%)",
  "hsl(196 66% 40%)",
  "hsl(196 78% 32%)",
  "hsl(196 88% 26%)",
];

const NEXT_LEVEL: Record<AssessmentLevel, AssessmentLevel | null> = {
  thematic: "issue",
  issue: "goal",
  goal: "action",
  action: "enterprise",
  enterprise: null,
};

interface Crumb {
  level: AssessmentLevel;
  parentId: string | null;
  label: string;
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-3xl font-extrabold tabular-nums leading-none text-foreground">
          {value}
        </div>
        <div className="mt-2 text-[13px] text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

/** One count in the courses table: a mini-bar (scaled to the column max) + the
 *  value, coloured by metric — blue = courses of action, green = completed. */
function MetricCell({
  value,
  max,
  tone,
}: {
  value: number;
  max: number;
  tone: "info" | "success";
}) {
  return (
    <div className="flex items-center justify-end gap-2.5">
      <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-muted sm:block">
        <span
          className={cn("block h-full rounded-full", tone === "info" ? "bg-info" : "bg-success")}
          style={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }}
        />
      </span>
      <span
        className={cn(
          "min-w-6 text-right font-bold tabular-nums",
          tone === "info" ? "text-info" : "text-success",
        )}
      >
        {value}
      </span>
    </div>
  );
}

/** Lazily-loaded list of the enterprise names inside one breakdown bucket. */
function GroupEnterprises({
  group,
  bucketKey,
  filters,
}: {
  group: "sector" | "partner_group" | "step";
  bucketKey: string;
  filters: AssessmentFilterState;
}) {
  const { t } = useTranslation();
  const q = useGroupEnterprises(group, bucketKey, filters, true);
  const rows = q.data ?? [];
  return (
    <div className="ml-6 mt-1 border-l pl-3">
      {q.isLoading ? (
        <p className="py-1 text-xs text-muted-foreground">…</p>
      ) : q.isError ? (
        // A failed fetch must read as an error, not as an empty bucket.
        <p className="py-1 text-xs text-destructive">{t("reports.state.error")}</p>
      ) : rows.length === 0 ? (
        <p className="py-1 text-xs text-muted-foreground">{t("reports.state.empty")}</p>
      ) : (
        <ul className="space-y-0.5 py-1">
          {rows.map((e) => (
            <li key={e.id} className="truncate text-xs text-muted-foreground">
              {e.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A labelled horizontal-bar breakdown (sectors / partner groups). Each row
 *  expands to the enterprise names in that bucket (parity with the legacy
 *  accordion lists). */
function Breakdown({
  title,
  rows,
  emptyLabel,
  group,
  filters,
}: {
  title: string;
  rows: { name: string; count: number }[];
  emptyLabel: string;
  group: "sector" | "partner_group";
  filters: AssessmentFilterState;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="mb-3 font-semibold text-foreground">{title}</h3>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <ul className="space-y-2.5">
            {rows.map((row) => {
              const isOpen = open === row.name;
              return (
                <li key={row.name}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : row.name)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-3 text-left text-sm"
                  >
                    <ChevronRight
                      aria-hidden
                      className={cn(
                        "size-3.5 shrink-0 text-muted-foreground transition-transform",
                        isOpen && "rotate-90",
                      )}
                    />
                    <span className="w-36 shrink-0 truncate text-muted-foreground">
                      {row.name}
                    </span>
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <span
                        className="block h-full rounded-full bg-theme-economic"
                        style={{ width: `${(row.count / max) * 100}%` }}
                      />
                    </span>
                    <span className="w-8 shrink-0 text-right font-semibold tabular-nums">
                      {row.count}
                    </span>
                  </button>
                  {isOpen && (
                    <GroupEnterprises
                      group={group}
                      bucketKey={row.name}
                      filters={filters}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function AssessmentFilters({
  value,
  onChange,
  onExport,
}: {
  value: AssessmentFilterState;
  onChange: (next: AssessmentFilterState) => void;
  onExport: () => void;
}) {
  const { t } = useTranslation();
  const { data: options } = useFilterOptions();
  // Every list but the partner groups follows the other filters, as on the
  // other report tabs and the Django « Bilan personnalisable ».
  const facets = useReportFacets("assessment", value).data;
  const categoryOptions = facets?.sector_categories ?? [];
  const sectorOptions = facets?.sectors ?? [];
  const regionOptions = facets?.region ?? [];
  const mrcOptions = facets?.mrc ?? [];
  const cityOptions = facets?.city ?? [];
  const provinceOptions = facets?.province ?? [];
  const issueOptions = facets?.issues ?? [];
  const goalOptions = facets?.goals ?? [];
  const actionOptions = facets?.actions ?? [];
  const selectedSectors = value.sectors ?? [];

  const toggle = (key: "sectors" | "issues" | "goals" | "actions", id: string) => {
    const current = value[key] ?? [];
    onChange({
      ...value,
      [key]: current.includes(id) ? current.filter((v) => v !== id) : [...current, id],
    });
  };
  const toggleSector = (id: string) => toggle("sectors", id);

  const categoryName = categoryOptions.find(
    (c) => c.id === value.sector_categories,
  )?.name;

  const groupName = options?.partner_groups.find(
    (g) => String(g.id) === value.partner_group,
  )?.name;

  // What's set inside the advanced block, so it opens rather than hiding a
  // filter that is silently in force.
  const advancedCount =
    (value.city ? 1 : 0) +
    (value.mrc ? 1 : 0) +
    (value.province ? 1 : 0) +
    (value.issues?.length ?? 0) +
    (value.goals?.length ?? 0) +
    (value.actions?.length ?? 0) +
    (value.sectors?.length ?? 0) +
    (value.date_from ? 1 : 0) +
    (value.date_to ? 1 : 0);

  const chips: { key: string; label: string; remove: () => void }[] = [];
  if (value.partner_group && groupName)
    chips.push({
      key: "partner_group",
      label: `${t("reports.filters.partnerGroup")}: ${groupName}`,
      remove: () => onChange({ ...value, partner_group: undefined }),
    });
  if (value.city)
    chips.push({
      key: "city",
      label: `${t("reports.columns.city")}: ${value.city}`,
      remove: () => onChange({ ...value, city: undefined }),
    });
  if (value.region)
    chips.push({
      key: "region",
      label: `${t("reports.filters.region")}: ${value.region}`,
      remove: () => onChange({ ...value, region: undefined }),
    });
  if (value.mrc)
    chips.push({
      key: "mrc",
      label: `${t("reports.filters.mrc")}: ${value.mrc}`,
      remove: () => onChange({ ...value, mrc: undefined }),
    });
  if (value.province)
    chips.push({
      key: "province",
      label: `${t("reports.filters.province")}: ${value.province}`,
      remove: () => onChange({ ...value, province: undefined }),
    });
  if (value.date_from || value.date_to)
    chips.push({
      key: "dates",
      label: `${value.date_from ?? "…"} → ${value.date_to ?? "…"}`,
      remove: () => onChange({ ...value, date_from: undefined, date_to: undefined }),
    });
  if (value.sector_categories && categoryName)
    chips.push({
      key: "category",
      label: `${t("reports.filters.sectorCategory")}: ${categoryName}`,
      remove: () => onChange({ ...value, sector_categories: undefined }),
    });
  const pushChips = (
    key: "sectors" | "issues" | "goals" | "actions",
    opts: { id: string; name: string }[],
  ) => {
    for (const id of value[key] ?? []) {
      const name = opts.find((o) => o.id === id)?.name ?? id;
      chips.push({ key: `${key}-${id}`, label: name, remove: () => toggle(key, id) });
    }
  };
  pushChips("sectors", sectorOptions);
  pushChips("issues", issueOptions);
  pushChips("goals", goalOptions);
  pushChips("actions", actionOptions);

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        {/* Top row: label + reset + export (export lives here, no dead gap). */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t("reports.filters.heading")}
          </span>
          <span className="flex-1" />
          {chips.length > 0 && (
            <button
              type="button"
              onClick={() => onChange({})}
              className="text-xs font-semibold text-primary hover:underline"
            >
              {t("reports.filters.reset")}
            </button>
          )}
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download aria-hidden />
            {t("reports.csv.exportAssessment")}
          </Button>
        </div>

        {/* Everyday controls stay in view; the rest fold into "advanced". */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <FilterField label={t("reports.filters.partnerGroup")}>
            {(id) => (
              <select
                id={id}
                value={value.partner_group ?? ""}
                onChange={(e) =>
                  onChange({ ...value, partner_group: e.target.value || undefined })
                }
                className={cn(filterControlClass, "w-full text-muted-foreground")}
              >
                <option value="">{t("reports.filters.allPartnerGroups")}</option>
                {options?.partner_groups.map((group) => (
                  <option key={group.id} value={String(group.id)}>
                    {group.name}
                  </option>
                ))}
              </select>
            )}
          </FilterField>
          <FilterField label={t("reports.filters.region")}>
            {(id) => (
              <select
                id={id}
                value={value.region ?? ""}
                onChange={(e) =>
                  onChange({ ...value, region: e.target.value || undefined })
                }
                className={cn(filterControlClass, "w-full text-muted-foreground")}
              >
                <option value="">{t("reports.filters.allRegions")}</option>
                {regionOptions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
            )}
          </FilterField>
          <FilterField label={t("reports.filters.sectorCategory")}>
            {(id) => (
              <select
                id={id}
                value={value.sector_categories ?? ""}
                onChange={(e) =>
                  onChange({ ...value, sector_categories: e.target.value || undefined })
                }
                className={cn(filterControlClass, "w-full text-muted-foreground")}
              >
                <option value="">{t("reports.filters.allCategories")}</option>
                {categoryOptions.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            )}
          </FilterField>
        </div>

        <AdvancedFilters activeCount={advancedCount}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            <FilterField label={t("reports.filters.mrc")}>
              {(id) => (
                <select
                  id={id}
                  value={value.mrc ?? ""}
                  onChange={(e) =>
                    onChange({ ...value, mrc: e.target.value || undefined })
                  }
                  className={cn(filterControlClass, "w-full text-muted-foreground")}
                >
                  <option value="">{t("reports.filters.allMrcs")}</option>
                  {mrcOptions.map((mrc) => (
                    <option key={mrc.id} value={mrc.id}>
                      {mrc.name}
                    </option>
                  ))}
                </select>
              )}
            </FilterField>
            <FilterField label={t("reports.columns.city")}>
              {(id) => (
                <select
                  id={id}
                  value={value.city ?? ""}
                  onChange={(e) => onChange({ ...value, city: e.target.value || undefined })}
                  className={cn(filterControlClass, "w-full text-muted-foreground")}
                >
                  <option value="">{t("reports.filters.allCities")}</option>
                  {cityOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              )}
            </FilterField>
            <FilterField label={t("reports.filters.province")}>
              {(id) => (
                <select
                  id={id}
                  value={value.province ?? ""}
                  onChange={(e) => onChange({ ...value, province: e.target.value || undefined })}
                  className={cn(filterControlClass, "w-full text-muted-foreground")}
                >
                  <option value="">{t("reports.filters.allProvinces")}</option>
                  {provinceOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              )}
            </FilterField>
            <FilterField label={t("reports.filters.sectors")}>
              {(id) => (
                <MultiSelectFilter
                  id={id}
                  placeholder={t("reports.filters.allSectors")}
                  options={sectorOptions}
                  selected={selectedSectors}
                  onToggle={toggleSector}
                />
              )}
            </FilterField>
            <FilterField
              label={t("reports.filters.dateFrom")}
              error={
                // YYYY-MM strings compare as months. The API refuses the
                // range too; this says why, under the field, as Django does.
                value.date_from && value.date_to && value.date_from > value.date_to
                  ? t("reports.filters.dateOrder")
                  : undefined
              }
            >
              {(id) => (
                <MonthInput
                  id={id}
                  value={value.date_from}
                  onChange={(month) => onChange({ ...value, date_from: month })}
                />
              )}
            </FilterField>
            <FilterField label={t("reports.filters.dateTo")}>
              {(id) => (
                <MonthInput
                  id={id}
                  value={value.date_to}
                  onChange={(month) => onChange({ ...value, date_to: month })}
                />
              )}
            </FilterField>
            <FilterField label={t("reports.filters.issues")}>
              {(id) => (
                <MultiSelectFilter
                  id={id}
                  placeholder={t("reports.filters.allIssues")}
                  options={issueOptions}
                  selected={value.issues ?? []}
                  onToggle={(nodeId) => toggle("issues", nodeId)}
                />
              )}
            </FilterField>
            <FilterField label={t("reports.filters.goals")}>
              {(id) => (
                <MultiSelectFilter
                  id={id}
                  placeholder={t("reports.filters.allGoals")}
                  options={goalOptions}
                  selected={value.goals ?? []}
                  onToggle={(nodeId) => toggle("goals", nodeId)}
                />
              )}
            </FilterField>
            <FilterField label={t("reports.filters.actions")}>
              {(id) => (
                <MultiSelectFilter
                  id={id}
                  placeholder={t("reports.filters.allActions")}
                  options={actionOptions}
                  selected={value.actions ?? []}
                  onToggle={(nodeId) => toggle("actions", nodeId)}
                />
              )}
            </FilterField>
          </div>
        </AdvancedFilters>

        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            {chips.map((chip) => (
              <FilterChip key={chip.key} label={chip.label} onRemove={chip.remove} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AssessmentView() {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<AssessmentFilterState>({});
  // Drill-down stack; the last crumb is the level currently charted.
  const [trail, setTrail] = useState<Crumb[]>([
    { level: "thematic", parentId: null, label: "" },
  ]);
  const current: Crumb = trail[trail.length - 1] ?? {
    level: "thematic",
    parentId: null,
    label: "",
  };

  const summary = useAssessmentSummary(filters);
  const data = useAssessmentData(filters, current.level, current.parentId);
  // Which progress step is expanded to its enterprise names.
  const [stepOpen, setStepOpen] = useState<string | null>(null);

  const monthFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { month: "short", year: "2-digit" }),
    [i18n.language],
  );

  const stepDonut = useMemo(() => {
    const steps = summary.data?.progress_steps ?? {};
    return PROGRESS_STEPS.map((step, index) => ({
      name: t(`reports.steps.${step}`),
      value: steps[step] ?? 0,
      color: STEP_RAMP[index],
    }));
  }, [summary.data, t]);

  const monthlyBars = useMemo(
    () =>
      (summary.data?.monthly_sign_ups ?? []).map((m) => ({
        label: monthFmt.format(new Date(m.month)),
        count: m.count,
      })),
    [summary.data, monthFmt],
  );

  const drill = (id: string, label: string) => {
    const next = NEXT_LEVEL[current.level];
    if (!next) return;
    setTrail([...trail, { level: next, parentId: id, label }]);
  };

  const handleExport = async () => {
    try {
      await downloadCsv("/api/v1/reports/assessment/csv/", filters, "bilan.csv");
    } catch {
      toast.error(t("reports.csv.failed"));
    }
  };

  const totals = summary.data?.totals ?? {};

  return (
    <div className="space-y-6">
      <AssessmentFilters value={filters} onChange={setFilters} onExport={handleExport} />

      <QueryState isLoading={summary.isLoading} isError={summary.isError}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard
            label={t("reports.assessment.signUps")}
            value={summary.data?.total_enterprises ?? 0}
          />
          <StatCard
            label={t("reports.assessment.completedQuestionnaire")}
            value={totals.completed_questionnaire ?? 0}
          />
          <StatCard
            label={t("reports.assessment.completedActionPlan")}
            value={totals.completed_action_plan ?? 0}
          />
          <StatCard
            label={t("reports.assessment.publishedCommunication")}
            value={totals.published_communication ?? 0}
          />
          <StatCard
            label={t("reports.assessment.activeUsers")}
            value={totals.active_subscription ?? 0}
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Monthly sign-ups */}
          <Card>
            <CardContent className="p-4">
              <h3 className="mb-2 font-semibold text-foreground">
                {t("reports.assessment.signUpsPerMonth")}
              </h3>
              {monthlyBars.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  {t("reports.state.empty")}
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={monthlyBars} margin={{ bottom: 20, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar
                      dataKey="count"
                      name={t("reports.assessment.signUps")}
                      fill="hsl(var(--theme-economic))"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Progression: donut by step + per-step list */}
          <Card>
            <CardContent className="p-4">
              <h3 className="mb-2 font-semibold text-foreground">
                {t("reports.assessment.progression")}
              </h3>
              <div className="flex flex-wrap items-center gap-4">
                <ResponsiveContainer width={180} height={200}>
                  <PieChart>
                    <Pie
                      data={stepDonut.filter((d) => d.value > 0)}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={48}
                      outerRadius={82}
                      paddingAngle={2}
                    >
                      {stepDonut
                        .filter((d) => d.value > 0)
                        .map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <ul className="min-w-52 flex-1 space-y-1.5">
                  {stepDonut.map((step, index) => {
                    const stepKey: string = PROGRESS_STEPS[index] ?? "";
                    const isOpen = stepOpen === stepKey;
                    return (
                      <li key={step.name}>
                        <button
                          type="button"
                          onClick={() => setStepOpen(isOpen ? null : stepKey)}
                          aria-expanded={isOpen}
                          className="flex w-full items-center gap-2.5 text-left text-sm"
                        >
                          <ChevronRight
                            aria-hidden
                            className={cn(
                              "size-3 shrink-0 text-muted-foreground transition-transform",
                              isOpen && "rotate-90",
                            )}
                          />
                          <span
                            className="size-2.5 shrink-0 rounded-full"
                            style={{ background: STEP_RAMP[index] }}
                            aria-hidden
                          />
                          <span className="text-muted-foreground">
                            {index} · {step.name}
                          </span>
                          <span className="ml-auto font-semibold tabular-nums">
                            {step.value}
                          </span>
                        </button>
                        {isOpen && (
                          <GroupEnterprises
                            group="step"
                            bucketKey={stepKey}
                            filters={filters}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <p className="mt-4 flex items-center gap-2 rounded-lg border border-info/30 bg-info/10 px-3.5 py-2.5 text-sm text-foreground">
                <MousePointerClick className="size-4 shrink-0 text-info" aria-hidden />
                {t("reports.assessment.progressionHint")}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Courses of action & achievements — drillable table (theme → issue →
            goal → action → companies), mirroring the legacy accordion table. */}
        <Card>
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold text-foreground">
                {t("reports.assessment.byThematic")}
              </h3>
              {trail.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTrail(trail.slice(0, -1))}
                >
                  <ChevronLeft aria-hidden />
                  {t("reports.assessment.back")}
                </Button>
              )}
            </div>

            {/* breadcrumb: drill path; click a crumb to jump back up */}
            <nav className="mt-2 flex flex-wrap items-center gap-1.5 text-sm">
              {trail.map((crumb, i) => {
                const isLast = i === trail.length - 1;
                const label =
                  i === 0
                    ? t("reports.assessment.levels.thematic")
                    : crumb.label || t(`reports.assessment.levels.${crumb.level}`);
                return (
                  <span key={i} className="flex items-center gap-1.5">
                    {i > 0 && (
                      <span className="text-muted-foreground/50" aria-hidden>
                        ›
                      </span>
                    )}
                    {isLast ? (
                      <span className="font-semibold text-foreground">{label}</span>
                    ) : (
                      <button
                        type="button"
                        className="font-medium text-muted-foreground hover:text-foreground hover:underline"
                        onClick={() => setTrail(trail.slice(0, i + 1))}
                      >
                        {label}
                      </button>
                    )}
                  </span>
                );
              })}
            </nav>

            <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span className="size-2.5 rounded-sm bg-info" aria-hidden />
                {t("reports.assessment.courseOfAction")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="size-2.5 rounded-sm bg-success" aria-hidden />
                {t("reports.assessment.carriedOut")}
              </span>
            </div>

            <div className="mt-3">
              <QueryState
                isLoading={data.isLoading}
                isError={data.isError}
                isEmpty={data.data?.length === 0}
              >
                {current.level === "enterprise" ? (
                  // Leaf: the companies that answered this action, with the answer kind.
                  <ul className="divide-y divide-border rounded-lg border border-border">
                    {(data.data ?? []).map((row) => (
                      <li
                        key={row.id}
                        className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
                      >
                        <span className="truncate font-medium text-foreground">
                          {row.name}
                        </span>
                        <span
                          className={cn(
                            "shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold",
                            row.carried_out_count
                              ? "bg-success/10 text-success"
                              : "bg-info/10 text-info",
                          )}
                        >
                          {row.carried_out_count
                            ? t("reports.assessment.carriedOut")
                            : t("reports.assessment.courseOfAction")}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  (() => {
                    const rows = data.data ?? [];
                    const maxCourse = Math.max(
                      1,
                      ...rows.map((r) => r.course_of_action_count),
                    );
                    const maxCarried = Math.max(
                      1,
                      ...rows.map((r) => r.carried_out_count),
                    );
                    const drillable = Boolean(NEXT_LEVEL[current.level]);
                    return (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="pb-2.5 pr-3 text-left text-xs font-bold uppercase tracking-wide text-muted-foreground">
                                {t(`reports.assessment.levels.${current.level}`)}
                              </th>
                              <th className="px-3 pb-2.5 text-right text-xs font-bold uppercase tracking-wide text-muted-foreground">
                                {t("reports.assessment.courseOfAction")}
                              </th>
                              <th className="pb-2.5 pl-3 text-right text-xs font-bold uppercase tracking-wide text-muted-foreground">
                                {t("reports.assessment.carriedOut")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row) => (
                              <tr
                                key={row.id}
                                className={cn(
                                  "border-b border-border last:border-0",
                                  drillable && "cursor-pointer hover:bg-secondary/50",
                                )}
                                onClick={() =>
                                  drillable && drill(row.id, row.name)
                                }
                                tabIndex={drillable ? 0 : undefined}
                                onKeyDown={
                                  drillable
                                    ? (e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          drill(row.id, row.name);
                                        }
                                      }
                                    : undefined
                                }
                              >
                                <td className="py-3 pr-3">
                                  <div className="flex items-center gap-3">
                                    {current.level === "thematic" && (
                                      <span
                                        className={cn(
                                          "h-6 w-1 shrink-0 rounded",
                                          thematicClasses(
                                            (row as { css_class?: string }).css_class,
                                          ).solid,
                                        )}
                                        aria-hidden
                                      />
                                    )}
                                    <span className="font-medium text-foreground">
                                      {row.name}
                                    </span>
                                    {drillable && (
                                      <ChevronRight
                                        className="ml-auto size-4 shrink-0 text-muted-foreground"
                                        aria-hidden
                                      />
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-3">
                                  <MetricCell
                                    value={row.course_of_action_count}
                                    max={maxCourse}
                                    tone="info"
                                  />
                                </td>
                                <td className="py-3 pl-3">
                                  <MetricCell
                                    value={row.carried_out_count}
                                    max={maxCarried}
                                    tone="success"
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()
                )}
              </QueryState>
            </div>

            {NEXT_LEVEL[current.level] ? (
              <p className="mt-3 flex items-center gap-2 rounded-lg border border-info/30 bg-info/10 px-3.5 py-2.5 text-sm text-foreground">
                <MousePointerClick className="size-4 shrink-0 text-info" aria-hidden />
                {t("reports.assessment.drilldownHint")}
              </p>
            ) : null}
          </CardContent>
        </Card>

        {/* Enterprises grouped by sector / by partner group */}
        <div className="grid gap-6 lg:grid-cols-2">
          <Breakdown
            title={t("reports.assessment.bySector")}
            rows={summary.data?.sectors ?? []}
            emptyLabel={t("reports.state.empty")}
            group="sector"
            filters={filters}
          />
          <Breakdown
            title={t("reports.assessment.byPartnerGroup")}
            rows={summary.data?.partner_groups ?? []}
            emptyLabel={t("reports.state.empty")}
            group="partner_group"
            filters={filters}
          />
        </div>
      </QueryState>
    </div>
  );
}
